import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Layers3,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  UsersRound,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { handleApiError } from '@/shared/api/requests';
import { useUser } from '@/shared/hooks/useUser';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { useProjectAreaTags, useProjectUsers } from '../hooks';
import type {
  CreatePunchItemWorkflowData,
  PunchItemWorkflow,
  PunchListPriority,
  Trade,
  TradePartnerMinimal,
} from '../types';

type StepDraft = {
  title: string;
  description: string;
  trade: string | null;
  responsibleTradePartner: number | null;
  priority: PunchListPriority;
  dueDays: number;
};

type LocationMode = 'single' | 'multiple';
type WorkflowContext = 'project' | 'hotspot';

export type PunchWorkflowDialogSubmission =
  | {
      mode: 'update';
      definition: CreatePunchItemWorkflowData;
    }
  | {
      mode: 'start';
      definition: CreatePunchItemWorkflowData;
      locationMode: LocationMode | 'hotspot';
      locations: number[];
      punchManager: number | null;
      finalApprover: number | null;
      isPrivate: boolean;
    };

const emptyStep = (): StepDraft => ({
  title: '',
  description: '',
  trade: null,
  responsibleTradePartner: null,
  priority: 'MEDIUM',
  dueDays: 0,
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  workflow?: PunchItemWorkflow | null;
  defaultSaveAsTemplate?: boolean;
  heading?: string;
  trades: Trade[];
  tradePartners: TradePartnerMinimal[];
  onSubmit: (data: PunchWorkflowDialogSubmission) => Promise<unknown>;
  context?: WorkflowContext;
}

export const PunchItemWorkflowDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  projectId,
  workflow,
  defaultSaveAsTemplate = false,
  heading,
  trades,
  tradePartners,
  onSubmit,
  context = 'project',
}) => {
  const isHotspotContext = context === 'hotspot';
  const { data: locations = [] } = useProjectAreaTags(isHotspotContext ? undefined : projectId);
  const { data: users = [] } = useProjectUsers(projectId);
  const { user: currentUser } = useUser();
  const isEditing = Boolean(workflow);
  const [wizardStep, setWizardStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState<boolean | null>(null);
  const [locationMode, setLocationMode] = useState<LocationMode | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [manager, setManager] = useState('none');
  const [managerChoiceInitialized, setManagerChoiceInitialized] = useState(false);
  const [approver, setApprover] = useState('none');
  const [isPrivate, setIsPrivate] = useState(false);
  const [stages, setStages] = useState<StepDraft[][]>([[emptyStep()]]);
  const [showTimingChoice, setShowTimingChoice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const wizardSteps = isHotspotContext
    ? [
        { step: 1, label: 'Workflow type' },
        { step: 3, label: 'Work order' },
        { step: 4, label: 'Review' },
      ]
    : [
        { step: 1, label: 'Workflow type' },
        { step: 2, label: 'Locations' },
        { step: 3, label: 'Work order' },
        { step: 4, label: 'Review' },
      ];

  useEffect(() => {
    if (!open) return;
    setError(null);
    setWizardStep(workflow ? 3 : 1);
    setName(workflow?.name ?? '');
    setDescription(workflow?.description ?? '');
    setSaveAsTemplate(workflow?.is_template ?? (defaultSaveAsTemplate ? true : null));
    setLocationMode(null);
    setSelectedLocations([]);
    setManager('none');
    setManagerChoiceInitialized(false);
    setApprover('none');
    setIsPrivate(false);
    setShowTimingChoice(false);
    if (!workflow) {
      setStages([[emptyStep()]]);
      return;
    }
    const grouped = new Map<number, StepDraft[]>();
    workflow.steps.forEach((step) => {
      const group = grouped.get(step.stage) ?? [];
      group.push({
        title: step.title,
        description: step.description,
        trade: step.trade,
        responsibleTradePartner: step.responsible_trade_partner,
        priority: step.priority,
        dueDays: step.due_days_after_activation,
      });
      grouped.set(step.stage, group);
    });
    setStages([...grouped.entries()].sort(([a], [b]) => a - b).map(([, steps]) => steps));
  }, [defaultSaveAsTemplate, open, workflow]);

  const coordinatorUsers = useMemo(
    () => {
      if (!currentUser) return users;
      const currentUserId = Number(currentUser.id);
      if (!Number.isFinite(currentUserId)) return users;
      if (users.some((user) => user.id === currentUserId)) return users;
      return [{
        id: currentUserId,
        email: currentUser.email,
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
      }, ...users];
    },
    [currentUser, users],
  );
  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => {
      const aLabel = a.layout_name ? `${a.layout_name} - ${a.name}` : a.name;
      const bLabel = b.layout_name ? `${b.layout_name} - ${b.name}` : b.name;
      return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: 'base' });
    }),
    [locations],
  );

  useEffect(() => {
    if (!open || managerChoiceInitialized || !currentUser) return;
    const currentUserId = Number(currentUser.id);
    if (!Number.isFinite(currentUserId)) return;
    setManager(String(currentUserId));
    setManagerChoiceInitialized(true);
  }, [currentUser, managerChoiceInitialized, open]);

  const buildDefinition = (): CreatePunchItemWorkflowData => ({
    project: projectId,
    name: name.trim(),
    description: description.trim(),
    is_template: saveAsTemplate === true,
    steps: stages.flatMap((stage, stageIndex) => stage.map((step, stepIndex) => ({
      stage: stageIndex + 1,
      position: stepIndex + 1,
      title: step.title.trim(),
      description: step.description.trim(),
      trade: step.trade,
      responsible_trade_partner: step.responsibleTradePartner && step.responsibleTradePartner > 0
        ? step.responsibleTradePartner
        : null,
      priority: step.priority,
      due_days_after_activation: Math.max(0, step.dueDays || 0),
    }))),
  });

  const updateStep = (stageIndex: number, stepIndex: number, patch: Partial<StepDraft>) => {
    setStages((current) => current.map((stage, index) =>
      index === stageIndex
        ? stage.map((step, position) => position === stepIndex ? { ...step, ...patch } : step)
        : stage
    ));
  };

  const updateResponsibleTrade = (stageIndex: number, stepIndex: number, tradeId: string | null) => {
    const matchingPartners = tradePartners.filter((candidate) => candidate.trade?.id === tradeId);
    updateStep(stageIndex, stepIndex, {
      trade: tradeId,
      responsibleTradePartner: matchingPartners.length === 1 ? matchingPartners[0].id : null,
    });
  };

  const removeStep = (stageIndex: number, stepIndex: number) => {
    setStages((current) => {
      if (current.length === 1 && current[0].length === 1) return current;
      const next = current.map((stage) => [...stage]);
      next[stageIndex].splice(stepIndex, 1);
      return next.filter((stage) => stage.length > 0);
    });
  };

  const addTask = (timing: 'together' | 'after') => {
    setStages((current) => timing === 'together'
      ? current.map((stage, index) => index === current.length - 1 ? [...stage, emptyStep()] : stage)
      : [...current, [emptyStep()]]
    );
    setShowTimingChoice(false);
  };

  const toggleLocation = (locationId: number, checked: boolean) => {
    setSelectedLocations((current) => checked
      ? [...current, locationId]
      : current.filter((id) => id !== locationId));
  };

  const goNext = () => {
    setError(null);
    if (wizardStep === 1 && saveAsTemplate === null) {
      setError('Choose whether to use this workflow once or save it as a template.');
      return;
    }
    if (wizardStep === 2) {
      if (!locationMode) {
        setError('Choose whether the work is happening in one location or several.');
        return;
      }
      const minimum = locationMode === 'multiple' ? 2 : 1;
      if (selectedLocations.length < minimum) {
        setError(locationMode === 'multiple'
          ? 'Choose at least two locations to coordinate together.'
          : 'Choose where this work is happening.');
        return;
      }
    }
    if (wizardStep === 3) {
      if (!name.trim() || stages.some((stage) => stage.some((step) => !step.title.trim()))) {
        setError('Name this work and describe every task before continuing.');
        return;
      }
    }
    setWizardStep((current) => Math.min(4, current + 1));
  };

  const submitWorkflow = async () => {
    if (!name.trim() || stages.some((stage) => stage.some((step) => !step.title.trim()))) {
      setError('Name this work and describe every task before continuing.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const definition = buildDefinition();
      if (workflow) {
        await onSubmit({ mode: 'update', definition });
      } else {
        if (!isHotspotContext && (!locationMode || selectedLocations.length === 0)) return;
        await onSubmit({
          mode: 'start',
          definition,
          locationMode: isHotspotContext ? 'hotspot' : locationMode!,
          locations: selectedLocations,
          punchManager: manager === 'none' ? null : Number(manager),
          finalApprover: approver === 'none' ? null : Number(approver),
          isPrivate,
        });
      }
      onOpenChange(false);
    } catch (submissionError) {
      setError(handleApiError(submissionError));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!workflow && wizardStep < 4) {
      goNext();
      return;
    }
    void submitWorkflow();
  };

  const selectLocationMode = (mode: LocationMode) => {
    setLocationMode(mode);
    setSelectedLocations([]);
    setError(null);
  };

  const selectPlanType = (shouldSave: boolean) => {
    setSaveAsTemplate(shouldSave);
    setError(null);
    setWizardStep(isHotspotContext ? 3 : 2);
  };

  const selectSingleLocation = (locationId: number) => {
    setSelectedLocations([locationId]);
    setError(null);
    setWizardStep(3);
  };

  const goBack = () => {
    setError(null);
    if (wizardStep === 2 && locationMode) {
      setLocationMode(null);
      setSelectedLocations([]);
      return;
    }
    if (wizardStep === 3 && isHotspotContext) {
      setWizardStep(1);
      return;
    }
    setWizardStep((current) => current - 1);
  };

  const choiceCard = (
    selected: boolean,
    onClick: () => void,
    icon: React.ReactNode,
    title: string,
    copy: string,
  ) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'group relative flex min-h-40 flex-col items-start rounded-[1.375rem] border p-5 text-left transition-all',
        selected
          ? 'border-primary bg-primary/10 shadow-[0_0_0_1px_hsl(var(--primary)/0.3)]'
          : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50',
      )}
    >
      <span className={cn(
        'grid size-11 place-items-center rounded-xl transition-colors',
        selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:text-foreground',
      )}>
        {icon}
      </span>
      <span className="mt-5 font-display text-lg font-bold">{title}</span>
      <span className="mt-1 text-sm leading-6 text-muted-foreground">{copy}</span>
      {selected && (
        <span className="absolute right-4 top-4 grid size-6 place-items-center rounded-full bg-primary text-primary-foreground">
          <Check className="size-4" />
        </span>
      )}
    </button>
  );

  const workBuilder = (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="workflow-name">What are you coordinating?</Label>
          <Input
            id="workflow-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Kitchen device relocation"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="workflow-description">What should the team know?</Label>
          <Input
            id="workflow-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Optional context for everyone involved"
          />
        </div>
      </div>

      <div className="space-y-4">
        {stages.map((stage, stageIndex) => (
          <section key={stageIndex} className="relative rounded-[1.375rem] border border-border bg-muted/35 p-4 sm:p-5">
            <div className="mb-4 flex items-start gap-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                {stageIndex + 1}
              </span>
              <div>
                <h3 className="font-display font-bold">
                  {stageIndex === 0 ? 'What needs to happen first?' : 'What happens after the previous work is finished?'}
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {stage.length > 1
                    ? `${stage.length} tasks can start at the same time.`
                    : stageIndex === 0 ? 'This task starts the workflow.' : 'This task waits for the group above.'}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {stage.map((step, stepIndex) => (
                <div key={stepIndex} className="rounded-xl border border-border bg-card p-4">
                  {stage.length > 1 && (
                    <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-primary">
                      Can start at the same time
                    </p>
                  )}
                  <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Label htmlFor={`workflow-task-${stageIndex}-${stepIndex}`}>What needs to be done?</Label>
                      <Input
                        id={`workflow-task-${stageIndex}-${stepIndex}`}
                        value={step.title}
                        onChange={(event) => updateStep(stageIndex, stepIndex, { title: event.target.value })}
                        placeholder="e.g. Mark the tiles that need to be removed"
                      />
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeStep(stageIndex, stepIndex)}
                      aria-label="Remove task"
                      disabled={stages.length === 1 && stage.length === 1}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-2">
                      <Label>Type of work</Label>
                      <Select value={step.trade ?? 'none'} onValueChange={(value) => updateResponsibleTrade(stageIndex, stepIndex, value === 'none' ? null : value)}>
                        <SelectTrigger><SelectValue placeholder="No trade" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No trade selected</SelectItem>
                          {trades.map((trade) => <SelectItem key={trade.id} value={trade.id}>{trade.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Who should do it?</Label>
                      <Select
                        value={step.responsibleTradePartner && step.responsibleTradePartner > 0 ? String(step.responsibleTradePartner) : 'none'}
                        onValueChange={(value) => {
                          const partnerId = Number(value);
                          updateStep(stageIndex, stepIndex, {
                            responsibleTradePartner: value !== 'none' && Number.isInteger(partnerId) && partnerId > 0
                              ? partnerId
                              : null,
                          });
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="No partner" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No partner selected</SelectItem>
                          {tradePartners
                            .filter((partner) => !step.trade || partner.trade?.id === step.trade)
                            .map((partner) => <SelectItem key={partner.id} value={String(partner.id)}>{partner.company_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={step.priority} onValueChange={(value) => updateStep(stageIndex, stepIndex, { priority: value as PunchListPriority })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LOW">Low</SelectItem>
                          <SelectItem value="MEDIUM">Medium</SelectItem>
                          <SelectItem value="HIGH">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Days to complete</Label>
                      <Input type="number" min={0} value={step.dueDays} onChange={(event) => updateStep(stageIndex, stepIndex, { dueDays: Number(event.target.value) })} />
                    </div>
                  </div>
                  <Textarea
                    className="mt-3"
                    value={step.description}
                    onChange={(event) => updateStep(stageIndex, stepIndex, { description: event.target.value })}
                    placeholder="Optional instructions, measurements, or details"
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {showTimingChoice ? (
        <div className="rounded-[1.375rem] border border-primary/40 bg-primary/5 p-5">
          <p className="font-display text-lg font-bold">When can the next task start?</p>
          <p className="mt-1 text-sm text-muted-foreground">This choice controls whether crews can work together or must wait for a handoff.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <button type="button" onClick={() => addTask('together')} className="rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/60 hover:bg-muted/50">
              <UsersRound className="size-5 text-primary" />
              <span className="mt-3 block font-semibold">At the same time</span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">It can begin with the current group of work.</span>
            </button>
            <button type="button" onClick={() => addTask('after')} className="rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/60 hover:bg-muted/50">
              <ArrowRight className="size-5 text-primary" />
              <span className="mt-3 block font-semibold">After this work is finished</span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">It becomes the next handoff in the workflow.</span>
            </button>
          </div>
          <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => setShowTimingChoice(false)}>Never mind</Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowTimingChoice(true)}
          disabled={stages.some((stage) => stage.some((step) => !step.title.trim()))}
        >
          <Plus className="mr-2 size-4" /> Add another task
        </Button>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{heading ?? (workflow ? 'Edit Workflow Template' : 'New Workflow')}</DialogTitle>
          <DialogDescription>
            {workflow
              ? 'Update the tasks and handoffs in this workflow template.'
              : 'Answer a few questions, then put the workflow tasks in the order crews should tackle them.'}
          </DialogDescription>
        </DialogHeader>

        {!isEditing && (
          <ol className={cn('grid gap-2', isHotspotContext ? 'grid-cols-3' : 'grid-cols-4')} aria-label="Setup progress">
            {wizardSteps.map(({ step, label }, index) => {
              const activeIndex = wizardSteps.findIndex((item) => item.step === wizardStep);
              const active = step === wizardStep;
              const complete = index < activeIndex;
              return (
                <li key={label} className="min-w-0">
                  <div className={cn('h-1 rounded-full', index <= activeIndex ? 'bg-primary' : 'bg-muted')} />
                  <span className={cn('mt-2 block truncate text-[11px] font-semibold', active ? 'text-foreground' : 'text-muted-foreground')}>
                    {complete ? 'Done' : label}
                  </span>
                </li>
              );
            })}
          </ol>
        )}

        <form onSubmit={handleFormSubmit} className="space-y-6">
          {!isEditing && wizardStep === 1 && (
            <section className="space-y-5 py-2">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Choose the workflow type</p>
                <h2 className="mt-2 font-display text-2xl font-extrabold">
                  Save this workflow as a template?
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {isHotspotContext ? 'You can start this issue now either way.' : 'You can start the work now either way.'}
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {choiceCard(
                  saveAsTemplate === false,
                  () => selectPlanType(false),
                  <Sparkles className="size-5" />,
                  isHotspotContext ? 'No, only for this issue' : 'Just this once',
                  'Use this workflow once without saving it as a template.',
                )}
                {choiceCard(
                  saveAsTemplate === true,
                  () => selectPlanType(true),
                  <RefreshCw className="size-5" />,
                  isHotspotContext ? 'Yes, save it for next time' : 'Yes, save it for reuse',
                  'Save this workflow as a template so it is ready for future work.',
                )}
              </div>
            </section>
          )}

          {!isEditing && wizardStep === 2 && (
            <section className="space-y-5 py-2">
              {!locationMode && (
                <>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Next, choose the scope</p>
                    <h2 className="mt-2 font-display text-2xl font-extrabold">Where will this work happen?</h2>
                    <p className="mt-2 text-sm text-muted-foreground">Several locations stay together at each handoff, so crews can handle them in one visit.</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {choiceCard(
                      false,
                      () => selectLocationMode('single'),
                      <MapPin className="size-5" />,
                      'One location',
                      'Start one set of tasks for one unit, room, or area.',
                    )}
                    {choiceCard(
                      false,
                      () => selectLocationMode('multiple'),
                      <Layers3 className="size-5" />,
                      'Several locations',
                      'Keep the same crews and handoffs coordinated across multiple areas.',
                    )}
                  </div>
                </>
              )}

              {locationMode === 'single' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">One location</p>
                    <h2 className="mt-2 font-display text-2xl font-extrabold">Which location?</h2>
                    <p className="mt-2 text-sm text-muted-foreground">Selecting a location takes you straight to the work order.</p>
                  </div>
                  <div className="space-y-2 rounded-[1.375rem] border border-border bg-muted/35 p-4">
                    <Label>Choose the location</Label>
                    <Select value={selectedLocations[0] ? String(selectedLocations[0]) : ''} onValueChange={(value) => selectSingleLocation(Number(value))}>
                    <SelectTrigger><SelectValue placeholder="Select a unit, room, or area" /></SelectTrigger>
                    <SelectContent>
                      {sortedLocations.map((location) => (
                        <SelectItem key={location.id} value={String(location.id)}>
                          {location.layout_name ? `${location.layout_name} - ${location.name}` : location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {locationMode === 'multiple' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Several locations</p>
                    <h2 className="mt-2 font-display text-2xl font-extrabold">Which locations?</h2>
                    <p className="mt-2 text-sm text-muted-foreground">Choose at least two locations that should move through the handoffs together.</p>
                  </div>
                  <div className="space-y-2 rounded-[1.375rem] border border-border bg-muted/35 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <Label>Choose every location</Label>
                      <span className="text-xs font-semibold text-muted-foreground">{selectedLocations.length} selected</span>
                    </div>
                    <div className="max-h-60 space-y-1 overflow-y-auto rounded-xl border border-border bg-card p-2">
                      {sortedLocations.length === 0 && <p className="p-3 text-sm text-muted-foreground">No project locations are available.</p>}
                      {sortedLocations.map((location) => (
                        <label key={location.id} className="flex cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium transition-colors hover:bg-muted">
                          <Checkbox
                            checked={selectedLocations.includes(location.id)}
                            onCheckedChange={(checked) => toggleLocation(location.id, checked === true)}
                          />
                          {location.layout_name ? `${location.layout_name} - ${location.name}` : location.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {(isEditing || wizardStep === 3) && (
            <section className="space-y-5 py-2">
              {!isEditing && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Now, build the handoffs</p>
                  <h2 className="mt-2 font-display text-2xl font-extrabold">What work needs to be done first?</h2>
                  <p className="mt-2 text-sm text-muted-foreground">Add tasks as you go, then tell us whether each one can start together or needs to wait.</p>
                </div>
              )}
              {workBuilder}
            </section>
          )}

          {!isEditing && wizardStep === 4 && (
            <section className="space-y-5 py-2">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">One last look</p>
                <h2 className="mt-2 font-display text-2xl font-extrabold">Ready to start the work?</h2>
                <p className="mt-2 text-sm text-muted-foreground">The first group will be assigned now. Later groups wait for each handoff.</p>
              </div>

              <div className="rounded-[1.375rem] border border-border bg-card p-5">
                <p className="font-display text-lg font-bold">{name}</p>
                {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  {isHotspotContext ? (
                    <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold">Selected hotspot</span>
                  ) : (
                    sortedLocations.filter((location) => selectedLocations.includes(location.id)).map((location) => (
                      <span key={location.id} className="rounded-full bg-muted px-3 py-1 text-xs font-semibold">
                        {location.name}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="workflow-manager">Who&apos;s coordinating the work?</Label>
                  <Select value={manager} onValueChange={(value) => { setManager(value); setManagerChoiceInitialized(true); }}>
                    <SelectTrigger id="workflow-manager"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None selected</SelectItem>
                      {coordinatorUsers.map((user) => <SelectItem key={user.id} value={String(user.id)}>{`${user.first_name} ${user.last_name}`.trim() || user.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Who gives final sign-off?</Label>
                  <Select value={approver} onValueChange={setApprover}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None selected</SelectItem>
                      {users.map((user) => <SelectItem key={user.id} value={String(user.id)}>{`${user.first_name} ${user.last_name}`.trim() || user.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted/35 px-4 py-3.5">
                  <Checkbox checked={saveAsTemplate === true} onCheckedChange={(checked) => setSaveAsTemplate(checked === true)} />
                  <span>
                    <span className="block text-sm font-semibold">Save as template</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">Keep this workflow ready for future work.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted/35 px-4 py-3.5">
                  <Checkbox checked={isPrivate} onCheckedChange={(checked) => setIsPrivate(checked === true)} />
                  <span>
                    <span className="block text-sm font-semibold">Limit who can see these tasks</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">Only selected project members will have access.</span>
                  </span>
                </label>
              </div>
            </section>
          )}

          {error && <p className="text-sm font-medium text-destructive">{error}</p>}

          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {!isEditing && wizardStep > 1 && (
                <Button type="button" variant="ghost" onClick={goBack}>
                  <ArrowLeft className="mr-2 size-4" /> Back
                </Button>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              {isEditing ? (
                <Button type="button" onClick={() => void submitWorkflow()} disabled={submitting}>
                  {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                  Save changes
                </Button>
              ) : wizardStep === 3 ? (
                <Button type="button" onClick={goNext}>
                  Continue <ArrowRight className="ml-2 size-4" />
                </Button>
              ) : wizardStep === 2 && locationMode === 'multiple' ? (
                <Button type="button" onClick={goNext}>
                  Continue <ArrowRight className="ml-2 size-4" />
                </Button>
              ) : (
                wizardStep === 4 ? (
                  <Button type="button" onClick={() => void submitWorkflow()} disabled={submitting}>
                    {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                    Start Workflow
                  </Button>
                ) : null
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
